import { useState, useEffect } from 'react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { submitLead, countryCodes, getDefaultCountryCode } from '@/services/api';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    contact_number: '',
    country_code: '+1',
    message: '',
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getDefaultCountryCode().then((code) => {
      setFormData((prev) => ({ ...prev, country_code: code }));
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await submitLead(formData, {
      source: 'contact_page',
      pageUrl: window.location.href,
      onSuccess: (data) => {
        if (data.customerId) {
          window.location.href = `/thanks?customerid=${data.customerId}`;
        }
      },
    });

    if (result.success) {
      setFormData({
        name: '',
        email: '',
        contact_number: '',
        country_code: '+1',
        message: '',
      });
    }

    setLoading(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Office Address',
      content: 'Old Middlefield Way, Mountain View, CA 94043',
    },
    {
      icon: Phone,
      title: 'Phone Number',
      content: '+1 (877) 946-4330',
    },
    {
      icon: Mail,
      title: 'Email Address',
      content: 'info@ipprotectionassociate.com',
    },
    // {
    //   icon: Clock,
    //   title: 'Business Hours',
    //   content: 'Monday - Friday: 9:00 AM - 6:00 PM EST',
    // },
  ];

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <Navigation />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="contact-hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FFF5E6] via-[#FFFAF0] to-[#FFF8DC] opacity-70"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#06214c] mb-6 leading-tight"
              style={{ fontFamily: 'Playfair Display' }}
              data-testid="contact-hero-title"
            >
              Get In Touch
            </h1>
            <p className="text-base sm:text-lg text-[#2C1810] max-w-2xl mx-auto" data-testid="contact-hero-subtitle">
              Have questions? We're here to help. Reach out to our team for expert guidance on trademark registration.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info and Form Section */}
      <section className="section" data-testid="contact-form-section">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#06214c] mb-8" style={{ fontFamily: 'Playfair Display' }}>
                Contact Information
              </h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <div key={index} className="flex items-start space-x-4" data-testid={`contact-info-${index}`}>
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#06214c] to-[#D4A574] rounded-full flex items-center justify-center">
                          <Icon className="text-white" size={24} />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-bold text-[#06214c] text-lg mb-1">{info.title}</h3>
                        <p className="text-gray-600">{info.content}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Contact Form */}
            <div className="card">
              <h2 className="text-3xl md:text-4xl font-bold text-[#06214c] mb-6" style={{ fontFamily: 'Playfair Display' }}>
                Reach Out To Us
              </h2>
              <p className="text-gray-600 mb-6">Please fill the form below for your queries and our representative will get back to you.</p>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="contact-page-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="contact-page-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="contact">Contact Number</Label>
                  <div className="flex space-x-2">
                    <Select
                      value={formData.country_code}
                      onValueChange={(value) => setFormData({ ...formData, country_code: value })}
                    >
                      <SelectTrigger className="w-[140px]" data-testid="contact-page-country-code">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {countryCodes.map((item) => (
                          <SelectItem key={item.code} value={item.code}>
                            {item.code} {item.country}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="contact"
                      type="tel"
                      value={formData.contact_number}
                      onChange={(e) => setFormData({ ...formData, contact_number: e.target.value })}
                      required
                      className="flex-1"
                      data-testid="contact-page-phone-input"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    required
                    data-testid="contact-page-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="contact-page-submit-btn"
                >
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}